import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'i18n';
import { useRouter } from 'next/router';
import { routes } from 'utils/routing';
import { makeEntitiesLoader } from 'hooks/makeSimpleLoader';
import { useSelector } from 'react-redux';
import { RootState } from 'store/rootReducer';
import { isFetchedStates, isFetchingStates } from 'store/utils';

import { AdminMarketsActions, AdminOrganizationsActions } from 'store/actions';
import { AdminMarketsSelectors, AdminOrganizationsSelectors } from 'store/selectors';

import AdminOrganizationsSelect from './AdminOrganizationsSelect';

const useMarketsState = makeEntitiesLoader(
  AdminMarketsActions.getAdminMarkets,
  AdminMarketsSelectors.adminMarketsState,
  false,
);
const params = {
  page_size: 100,
};

const useAdminOrganizationsState = makeEntitiesLoader(
  AdminOrganizationsActions.getAdminAllOrganizations,
  AdminOrganizationsSelectors.adminAllOrganizationsState,
  false,
);

const AdminOrganizationsSelectContainer: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [t] = useTranslation('admin');
  const router = useRouter();
  const organizationId = Number(router.query.organizationId);
  const queryMarketSlug = String(router.query.marketSlug);

  const marketsState = useMarketsState(() => params, []);
  const allOrganizationState = useAdminOrganizationsState();

  const marketFromQuery = useSelector((state: RootState) =>
    AdminMarketsSelectors.adminMarketBySlug(state, queryMarketSlug),
  );
  const chosenOrganization = useSelector((state: RootState) =>
    AdminOrganizationsSelectors.allOrganizationsById(state, organizationId),
  );
  const marketFromOrg = useSelector((state: RootState) =>
    AdminMarketsSelectors.adminMarketById(state, Number(chosenOrganization?.market_id)),
  );
  const [market, setMarket] = useState('');

  const marketFromSelect = useSelector((state: RootState) =>
    AdminMarketsSelectors.adminMarketById(state, Number(market)),
  );
  const filteredMarkets = marketsState.entities
    .filter((market) => allOrganizationState.entities.some((org) => Number(org.market_id) === Number(market.id)))
    .sort((a, b) => {
      // Group by is_local: local first
      if (a.is_local !== b.is_local) {
        return a.is_local ? -1 : 1;
      }
      // Only sort alphabetically if both are local
      if (a.is_local && b.is_local) {
        return a.slug.localeCompare(b.slug);
      }
      // Keep original order for non-local items
      return 0;
    });
  const filteredOrganizations = market
    ? allOrganizationState.entities.filter((org) => Number(org.market_id) === Number(market))
    : allOrganizationState.entities;

  const handleOrganizationSelect = (id: number) => {
    let route;
    if (id) {
      const marketFromId = allOrganizationState.entities.filter((item) => item.id === id);
      if (marketFromId.length > 0) {
        setMarket(String(marketFromId[0].market_id));
      }
      route = routes.adminOrganizationEvents(id);
    } else if (market) {
      route = routes.adminMarketEvents(marketFromSelect.slug);
    } else {
      route = routes.adminAllEvents();
    }
    router.push(route.href);
  };

  useEffect(() => {
    if (isFetchedStates(marketsState, allOrganizationState) && (marketFromQuery?.id || marketFromOrg?.id)) {
      setMarket(String(marketFromQuery?.id || marketFromOrg?.id));
    }
  }, [allOrganizationState, marketFromOrg?.id, marketFromQuery, marketsState]);

  return (
    <AdminOrganizationsSelect
      t={t}
      isFetching={isFetchingStates(marketsState, allOrganizationState)}
      organizationId={organizationId}
      onSelect={handleOrganizationSelect}
      options={filteredOrganizations}
      chosenOrganization={chosenOrganization}
      markets={filteredMarkets}
      market={market}
      onFilterChange={setMarket}
    />
  );
};

export default memo(AdminOrganizationsSelectContainer);
